import React from 'react'
import {
    RadialBarChart,
    RadialBar,
    PolarAngleAxis,
} from "recharts";

const InfoBlock = ({ title, render, children }) => {
    return (
        <div className="border w-full rounded-lg flex flex-col items-start justify-start gap-2 p-2 shadow">
            <div className="flex items-center justify-between w-full">
                <p className=' text-lg font-semibold'>{title}</p>
                {render && render()}
            </div>
            <div>
                {children}
            </div>
        </div>
    )
}

const ResultCard = ({ apiResponse }) => {

    // 🛡 FULL SAFE NORMALIZATION
    const topCode = apiResponse?.topCode || {}
    const alternativeCodes = apiResponse?.alternativeCodes || []
    const whyThisCode = apiResponse?.whyThisCode || {}
    const featureExtraction = apiResponse?.featureExtraction || []
    const exclusions = apiResponse?.exclusions || []
    const decisionLogic = apiResponse?.decisionLogic || {}

    const chartData = [
        {
            name: "Top Code",
            value: topCode?.confidence ?? 0,
            code: topCode?.code ?? "—",
            fill: "url(#topCodeGradient)"
        },
        {
            name: "Alternative Code",
            value: alternativeCodes?.[0]?.confidence ?? 0,
            code: alternativeCodes?.[0]?.code ?? "—",
            fill: "url(#alt1Gradient)"
        },
    ];

    if (!apiResponse || !topCode?.code) {
        return null;
    }

    return (
        <div className="flex flex-col items-start justify-center gap-4">
            <div className="w-full flex flex-col md:flex-row items-start md:items-center justify-between border-b py-2 mb-2">
                <h3 className='font-bold'>Result & Explanation</h3>
                <p className='text-sm text-gray-500'>Transparent decision path + confidence</p>
            </div>

            {/* Chart Section */}
            <div className="flex flex-col md:flex-row items-center justify-center gap-4 w-full shadow rounded-lg">
                <div className="relative">
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 p-6 size-24 flex items-center justify-center rounded-full bg-gray-800 shadow-xl text-white">
                        {topCode?.code}
                    </div>

                    <RadialBarChart
                        width={250}
                        height={250}
                        innerRadius="50%"
                        outerRadius="100%"
                        data={chartData}
                        startAngle={90}
                        barSize={16}
                        endAngle={-270}
                    >
                        <defs>
                            <linearGradient id="alt1Gradient" x1="0" y1="0" x2="1" y2="1">
                                <stop offset="0%" stopColor="#1c58f4" />
                                <stop offset="100%" stopColor="#6ec1ff" />
                            </linearGradient>
                            <linearGradient id="topCodeGradient" x1="0" y1="0" x2="1" y2="1">
                                <stop offset="0%" stopColor="#00944b" />
                                <stop offset="100%" stopColor="#00e676" />
                            </linearGradient>
                        </defs>

                        <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
                        <RadialBar
                            minAngle={15}
                            background
                            clockWise
                            dataKey="value"
                            cornerRadius={10}
                        />
                    </RadialBarChart>
                </div>
            </div>

            {/* Codes Section */}
            <div className="flex items-center flex-wrap gap-2">
                <div className="flex items-center gap-2 rounded-full px-3 py-1 border shadow text-sm font-semibold">
                    <span>Top Code</span>
                    <span>{topCode?.code}</span>
                    <span>({topCode?.confidence ?? 0}%)</span>
                </div>

                {alternativeCodes.map((item, i) => (
                    <div key={i} className="flex items-center gap-2 rounded-full px-3 py-1 border shadow text-sm font-semibold">
                        <span>Alternative Code {i + 1}</span>
                        <span>{item?.code}</span>
                        <span>({item?.confidence ?? 0}%)</span>
                    </div>
                ))}
            </div>



            {/* Top Code Row */}
            <div className="w-full flex items-center justify-between gap-2 rounded-full px-3 py-2 border shadow">
                <p className='flex flex-col md:flex-row items-center gap-2'>
            <span
    className="w-3 h-3 min-w-[12px] min-h-[12px] rounded-full flex-shrink-0"
    style={{ backgroundColor: '#00944b' }}
></span>

                    <span className="text-sm font-semibold whitespace-nowrap">
                        {topCode?.code}
                    </span>

                    <span className="text-sm text-gray-500">
                        {topCode?.description}
                    </span>
                </p>

                <span className="text-sm">
                    {topCode?.confidence ?? 0}%
                </span>
            </div>

            {/* Alternative Codes */}
            {alternativeCodes.map((item, i) => (
                <div key={i} className="w-full flex items-center justify-between gap-2 rounded-full px-3 py-2 border shadow">
                    <p className='flex flex-col md:flex-row items-center gap-2'>
                  <span
    className="w-3 h-3 min-w-[12px] min-h-[12px] rounded-full flex-shrink-0"
    style={{ backgroundColor: '#1c58f4' }}
></span>

                        <span className="text-sm font-semibold whitespace-nowrap">
                            {item?.code}
                        </span>

                        <span className="text-sm text-gray-500">
                            {item?.description}
                        </span>
                    </p>

                    <span className="text-sm">
                        {item?.confidence ?? 0}%
                    </span>
                </div>
            ))}

            {/* Why This Code Section */}
            <div className="w-full border rounded-xl p-4 space-y-2 shadow">
                <div className="flex items-center justify-between font-medium w-full">
                    <p>Why This Code?</p>
                    <p className='font-normal text-gray-500 text-sm'>Full Rationale and Explanations</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">

                    <InfoBlock title={"Exact Code Description"}>
                        <p>{whyThisCode?.exactCodeDescription ?? "Not available"}</p>
                        <div className="flex gap-2 flex-wrap mt-2">
                            {(whyThisCode?.keywords || []).map((tag, i) => (
                                <span key={i} className="px-3 border rounded-full bg-gray-100">{tag}</span>
                            ))}
                        </div>
                    </InfoBlock>

                    <InfoBlock title={"Feature Extraction"}>
                        <ul className='space-y-2'>
                            {featureExtraction.map((feature, i) => (
                                <li key={i} className='border-l-2 border-blue rounded-lg bg-gray-100 px-2 py-1'>
                                    <span className='font-semibold'>{feature?.label}:</span> {feature?.description}
                                </li>
                            ))}
                        </ul>
                    </InfoBlock>

                    <InfoBlock title={"Exclusions"}>
                        <ul className='list-disc list-inside'>
                            {exclusions.map((exclusion, i) => (
                                <li key={i}>
                                    <span className='font-semibold'>{exclusion?.code}</span> {exclusion?.description}
                                </li>
                            ))}
                        </ul>
                    </InfoBlock>

                    <InfoBlock
                        title={"Decision Tree & Weighing"}
                        render={() => (
                            <span className='px-2 py-1 rounded-full bg-gray-200 text-sm'>
                                Confidence: {decisionLogic?.confidenceScore ?? 0}%
                            </span>
                        )}
                    >
                        <ul className='list-disc list-inside'>
                            {(decisionLogic?.factors || []).map((factor, i) => (
                                <li key={i}>
                                    <span className='font-semibold'>{factor?.factor}</span> : ({factor?.weight}%)
                                </li>
                            ))}
                        </ul>
                    </InfoBlock>

                </div>
            </div>
        </div>
    )
}

export default ResultCard